import React, { useEffect, useState, useRef } from "react";

/**
 * APLUM signature — drawn as ONE continuous handwritten stroke.
 * Reference: artist's actual signature (star "A" + cursive "Plum.").
 * Animation: single long stroke-dashoffset reveal, slow & cinematic,
 * with subtle natural easing — feels like a hand writing in real time.
 */

// Continuous, single-path signature.
// Star A drawn first (BR → top → L → R → through middle = horizontal baseline),
// baseline extends right, P → l → u → m, ending in the period.
const SIGNATURE_PATH = "\
M 235 360 \
L 120 32 \
L 22 268 \
L 295 140 \
L 38 225 \
C 165 230 320 232 460 232 \
C 478 232 498 88 515 70 \
C 555 38 615 78 600 155 \
C 585 210 515 220 480 248 \
C 530 252 600 252 658 250 \
C 678 250 690 78 710 78 \
C 738 78 736 138 722 175 \
C 706 220 716 254 758 254 \
C 808 254 850 254 868 254 \
C 882 254 882 180 902 174 \
C 928 170 938 218 928 256 \
C 950 258 952 178 974 172 \
C 1002 168 1014 218 1018 256 \
C 1038 260 1042 178 1062 172 \
C 1090 168 1100 218 1098 254 \
C 1118 258 1124 178 1146 172 \
C 1174 168 1190 218 1184 256 \
C 1206 262 1228 224 1248 196 \
C 1272 170 1300 188 1300 224 \
C 1300 242 1280 254 1262 254 \
L 1306 254";

// Subtle pen-lifted period (drawn last as a tiny separate stroke to read clearly)
const PERIOD_PATH = "M 1336 252 L 1342 254";

// Approximate path length used for stroke-dasharray
const SIGNATURE_LENGTH = 3900;
const PERIOD_LENGTH = 14;

// Animation timing — long, deliberate, cinematic
const SIGNATURE_DURATION = 7.2; // seconds
const PERIOD_DURATION = 0.25;
const PERIOD_DELAY = SIGNATURE_DURATION + 0.25;
const INITIAL_DELAY = 0.6;

const SESSION_KEY = "aplum_signature_played_v3";

export const AnimatedSignature = ({ className = "" }) => {
    const [played, setPlayed] = useState(false);
    const sigRef = useRef(null);
    const dotRef = useRef(null);

    useEffect(() => {
        // Compute exact path length for crisp dash-offset rendering across browsers
        try {
            if (sigRef.current) {
                const len = sigRef.current.getTotalLength();
                sigRef.current.style.strokeDasharray = String(len);
                sigRef.current.style.strokeDashoffset = String(len);
            }
            if (dotRef.current) {
                const len = dotRef.current.getTotalLength();
                dotRef.current.style.strokeDasharray = String(len);
                dotRef.current.style.strokeDashoffset = String(len);
            }
        } catch (_) { /* noop */ }

        try {
            if (sessionStorage.getItem(SESSION_KEY) === "1") {
                setPlayed(true);
                return;
            }
            const t = setTimeout(() => {
                try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_) {}
            }, (PERIOD_DELAY + PERIOD_DURATION + 0.5) * 1000);
            return () => clearTimeout(t);
        } catch (_) { /* noop */ }
    }, []);

    const sharedStyle = {
        fill: "none",
        stroke: "#FFFFFF",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        filter: "drop-shadow(0 2px 24px rgba(0,0,0,0.55))",
    };

    return (
        <svg
            data-testid="aplum-signature"
            viewBox="0 0 1380 380"
            className={className}
            preserveAspectRatio="xMidYMid meet"
            aria-label="A_Plum signature"
        >
            <path
                ref={sigRef}
                d={SIGNATURE_PATH}
                strokeWidth={6}
                style={{
                    ...sharedStyle,
                    strokeDasharray: SIGNATURE_LENGTH,
                    strokeDashoffset: played ? 0 : SIGNATURE_LENGTH,
                    animation: played
                        ? "none"
                        : `aplumSigDraw ${SIGNATURE_DURATION}s cubic-bezier(0.42, 0, 0.32, 1) ${INITIAL_DELAY}s forwards`,
                }}
            />
            <path
                ref={dotRef}
                d={PERIOD_PATH}
                strokeWidth={10}
                style={{
                    ...sharedStyle,
                    strokeDasharray: PERIOD_LENGTH,
                    strokeDashoffset: played ? 0 : PERIOD_LENGTH,
                    animation: played
                        ? "none"
                        : `aplumSigDraw ${PERIOD_DURATION}s ease-out ${INITIAL_DELAY + PERIOD_DELAY}s forwards`,
                }}
            />
        </svg>
    );
};

export default AnimatedSignature;
